<script lang="ts">
	import ArticleCard from "@components/ArticleCard.svelte";
	import SubscriptionSideModal from "@components/SubscriptionSideModal.svelte";
	import Heading from "@components/common/Heading.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import AboutUsSection from "@components/sections/AboutUsSection.svelte";
	import CoffeeStoreSection from "@components/sections/CoffeeStoreSection.svelte";
	import HeroSection from "@components/sections/HeroSection.svelte";
	import IntroSections from "@components/sections/IntroSections.svelte";
	import ProductSection from "@components/sections/ProductSection.svelte";
	import WeAreRoasterySection from "@components/sections/WeAreRoasterySection.svelte";
	import { setConfiguratorState } from "@utils/configuratorContext.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getNewsAnchor } from "@core/schema/paths/getNewsAnchor.js";
	import { getPostsListingPath } from "@core/schema/paths/getPostsListingPath.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { buildReturnPolicies, buildShippingDetails, buildWebSite } from "../seo/sharedEntities.js";
	import translations from "./Homepage.translations.json" with { type: "json" };
	import type { HomepageProps } from "./HomepageProps.js";

	const translate = getTranslate(translations);

	const { newProducts, commonProducts, posts, subcategories, tenant }: HomepageProps = $props();
	const { WEB_URL_OR_LOCAL } = getGlobalSettings();
	const { countrySettings, exchangeRatesSetting } = getGlobalTemplateProps();
	const exchangeRates = exchangeRatesSetting?.exchangeRates ?? [];

	const webSiteSchema = buildWebSite(WEB_URL_OR_LOCAL);
	const returnPolicies = $derived(buildReturnPolicies(WEB_URL_OR_LOCAL, countrySettings));
	const shippingDetails = $derived(
		buildShippingDetails(WEB_URL_OR_LOCAL, countrySettings, tenant.defaultCurrency, exchangeRates),
	);

	const configuratorState = $state({ open: false });
	setConfiguratorState(configuratorState);
</script>

<StructuredData data={webSiteSchema} />
{#each returnPolicies as policy (policy["@id"])}
	<StructuredData data={policy} />
{/each}
{#each shippingDetails as detail (detail["@id"])}
	<StructuredData data={detail} />
{/each}

<Layout transparentHeader>
	<HeroSection />
	<IntroSections />

	<ProductSection
		{subcategories}
		products={newProducts}
		title={translate("titleNewProducts")}
		anchor={getNewsAnchor(tenant.locale)}
	/>

	<ProductSection
		{subcategories}
		products={commonProducts}
		title="[{getCategoryPath(tenant.locale, CategoryType.Coffee)}][[{translate('titleClassicOffer')}]]"
		class="bg-fcrbeige"
	/>
	<CoffeeStoreSection />
	<AboutUsSection />
	<WeAreRoasterySection />
	{#if posts.length > 0}
		<section class="py-14 sm:py-17 md:py-20 lg:py-23 xl:py-26">
			<Heading
				level={2}
				text="[{getPostsListingPath(tenant)}][[{translate('storiesHeading')}]]"
				class="heading-2 mb-8 md:mb-13 xl:mb-18"
			/>
			<div class="grid md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-14 md:gap-y-18 w-fit mx-auto">
				{#each posts as post (post.id)}
					<ArticleCard headingLevel={2} {post} huge={false} />
				{/each}
			</div>
		</section>
	{/if}
	<SubscriptionSideModal bind:show={configuratorState.open} />
</Layout>
